import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  tableauAiAdminAppSecretId,
  tableauAiConnectorName,
  tableauAiSessionTokenPrefix,
} from "@p0security/integrations-tableau-ai-shared/secret-manager";
import type { TableauAiComponents } from "@p0security/integrations-tableau-ai-shared/types";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  TableauAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-tableau-ai-gcloud",
  digest:
    "sha-c4202ae@sha256:2fd614def02f746ac93e0544441f1a8db39132f4418e362d94ebb8d2695614b1",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-tableau-ai-aws",
  digest:
    "sha-c4202ae@sha256:f11d1fa9aae4f2e155095c3109b4585ee9d008726b3da9f94770324108646d78",
} as const;

const printAdminAppJson = `printf '{"clientId":"%s","secretId":"%s","secret":"%s"}' "$TABLEAU_CLIENT_ID" "$TABLEAU_SECRET_ID" "$TABLEAU_SECRET"`;

const awsAdminAppSecretId = (site: string) =>
  secretProviderFor("aws").replacer(tableauAiAdminAppSecretId(site));

export const awsStoreAdminApp = (
  site: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    printAdminAppJson,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsAdminAppSecretId(site)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: { resource: "tableau", label: "Tableau" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: tableauAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      tableauAiSessionTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_app",
      itemTfId: tfId(itemId),
      secretId: awsAdminAppSecretId(itemId),
      label: "admin connected app",
    },
  });

const gcpAdminAppSecretId = (site: string) =>
  secretProviderFor("gcp").replacer(tableauAiAdminAppSecretId(site));

export const gcpStoreAdminApp = (
  site: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    printAdminAppJson,
    `| gcloud secrets versions add ${gcpAdminAppSecretId(site)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: { resource: "tableau", role: "Tableau", label: "Tableau" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      tableauAiSessionTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_app",
      itemTfId: tfId(itemId),
      secretId: gcpAdminAppSecretId(itemId),
      label: "admin connected app",
    },
  });
